import { useRef, useCallback, useEffect } from 'react';

// Patrón compartido para resize por arrastre. El bug que resuelve: el patrón repetido en 8+ archivos
// (`mousedown` → `addEventListener('mousemove'/'mouseup', ...)` en `window`/`document`, con el
// propio `mouseup` como única forma de desregistrarse) deja los listeners colgados para siempre
// si el usuario suelta el mouse fuera del viewport del navegador — un `mouseup` que ocurre sobre
// otra ventana/monitor nunca llega al DOM.
//
// Se usa Pointer Events con captura (`setPointerCapture`) en vez de mousemove/mouseup en
// `window`: un elemento con el puntero capturado sigue recibiendo `pointermove`/`pointerup`
// aunque el cursor salga del viewport, y `lostpointercapture` siempre dispara al soltar (o si el
// navegador retira la captura por cualquier motivo) — no hay forma de que quede colgado.
//
// Uso: `const start = useDragResize({ onMove, onEnd })`, y en el handle de resize:
// `onPointerDown={(e) => start(e, initialValue)}`. `onMove({ startX, startY, initialValue,
// moveEvent })` corre en cada `pointermove` y debe devolver el valor actual (para que `onEnd`
// lo reciba al soltar) — el cálculo real (delta, límites, qué se actualiza) queda en el caller,
// el hook solo orquesta el ciclo de vida de los listeners.
export function useDragResize({ onMove, onEnd }) {
    const onMoveRef = useRef(onMove);
    const onEndRef = useRef(onEnd);
    useEffect(() => {
        onMoveRef.current = onMove;
        onEndRef.current = onEnd;
    }, [onMove, onEnd]);

    const start = useCallback((e, initialValue) => {
        e.preventDefault();
        const target = e.currentTarget;
        const pointerId = e.pointerId;
        const startX = e.clientX;
        const startY = e.clientY;
        let finalValue = initialValue;

        try {
            target.setPointerCapture(pointerId);
        } catch {
            // Algunos elementos (ej. portados fuera del árbol normal) pueden rechazar la
            // captura — el resize sigue funcionando mientras el puntero no salga del target,
            // que es el comportamiento que ya tenían estos sitios antes de esta fase.
        }

        const handleMove = (moveEvent) => {
            finalValue = onMoveRef.current({ startX, startY, initialValue, moveEvent });
        };

        const handleEnd = () => {
            target.removeEventListener('pointermove', handleMove);
            target.removeEventListener('pointerup', handleEnd);
            target.removeEventListener('pointercancel', handleEnd);
            target.removeEventListener('lostpointercapture', handleEnd);
            onEndRef.current?.(finalValue);
        };

        target.addEventListener('pointermove', handleMove);
        target.addEventListener('pointerup', handleEnd);
        target.addEventListener('pointercancel', handleEnd);
        target.addEventListener('lostpointercapture', handleEnd);
    }, []);

    return start;
}
